import React, { useState } from "react";
import { Check, GraduationCap, LucideIcon } from "lucide-react";
import { getTutorial, TutorialKey } from "@/config/pageTutorials";
import TutorialVideoModal from "@/components/common/TutorialVideoModal";

export interface CampaignTypeDefinition {
    id: string;
    title: string;
    subtitle: string;
    description: string;
    icon: LucideIcon;
    gradient: string;
    bgLight: string;
    bgUnselected: string;
    borderActive: string;
    borderUnselected: string;
    ringColor: string;
    iconBg: string; // Class for selected state
    iconBgUnselected: string; // Class for unselected state
    iconColorUnselected: string;
    features: string[];
}

interface CampaignTypeCardProps {
    type: CampaignTypeDefinition;
    isSelected: boolean;
    onSelect: (id: string) => void;
    hasError?: boolean;
    id?: string;
    /** Tighten sizes so 4 cards fit comfortably in one row */
    compact?: boolean;
    /** Optional YouTube tutorial for this campaign type */
    tutorialKey?: TutorialKey | null;
}

export const CampaignTypeCard: React.FC<CampaignTypeCardProps> = ({
    type,
    isSelected,
    onSelect,
    hasError,
    id,
    compact = false,
    tutorialKey = null,
}) => {
    const Icon = type.icon;
    const [tutorialOpen, setTutorialOpen] = useState(false);
    const tutorial = tutorialKey ? getTutorial(tutorialKey) : null;
    const showTutorial = Boolean(tutorial?.youtubeId);

    return (
        <>
            <div
                id={id}
                className={`
        relative text-left rounded-2xl border-2 transition-all duration-300 ease-out
        w-full flex flex-col h-full overflow-hidden
        ${isSelected
                        ? `${type.borderActive} ${type.bgLight} ring-4 ${type.ringColor} shadow-lg`
                        : `${type.borderUnselected} ${type.bgUnselected} [&:has([data-card-body]:hover)]:bg-teal-50 [&:has([data-card-body]:hover)]:border-teal-400 [&:has([data-card-body]:hover)]:shadow-md`
                    }
        ${hasError ? "border-red-200" : ""}
      `}
            >
                {/* Selectable body — soft cyan on hover (preview before select); Tutorial hover is separate */}
                <div
                    data-card-body
                    role="button"
                    tabIndex={0}
                    onClick={() => onSelect(type.id)}
                    onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            onSelect(type.id);
                        }
                    }}
                    className={`
            group/body flex-1 flex flex-col cursor-pointer outline-none
            ${compact ? "p-4 pb-3" : "p-6 pb-4"}
          `}
                >
                    {/* Selected checkmark */}
                    {isSelected && (
                        <div className="absolute top-3 right-3 z-10">
                            <div className={`w-6 h-6 rounded-full ${type.iconBg} flex items-center justify-center shadow-md`}>
                                <Check className="w-3.5 h-3.5 text-white" strokeWidth={3} />
                            </div>
                        </div>
                    )}

                    {/* Icon */}
                    <div className={`
            ${compact ? "w-10 h-10 rounded-xl mb-3" : "w-14 h-14 rounded-2xl mb-4"}
            flex items-center justify-center transition-all duration-300 flex-shrink-0
            ${isSelected
                            ? type.iconBg + " shadow-lg"
                            : type.iconBgUnselected
                        }
          `}>
                        <Icon className={`${compact ? "w-5 h-5" : "w-7 h-7"} ${isSelected ? "text-white" : type.iconColorUnselected}`} />
                    </div>

                    {/* Content — full original text */}
                    <div className={`space-y-1.5 ${compact ? "pr-6" : "pr-8"} flex-1 flex flex-col`}>
                        <div className={compact ? "min-h-[44px]" : "min-h-[56px]"}>
                            <h3 className={`font-bold ${compact ? "text-base" : "text-lg"} ${isSelected ? "text-gray-900" : "text-gray-800"}`}>
                                {type.title}
                            </h3>
                            <p className={`font-medium leading-tight ${compact ? "text-xs" : "text-sm"} ${isSelected ? "text-gray-600" : "text-gray-500"}`}>
                                {type.subtitle}
                            </p>
                        </div>

                        <p className={`text-gray-500 leading-relaxed ${compact ? "text-xs min-h-[48px]" : "text-sm min-h-[80px]"}`}>
                            {type.description}
                        </p>

                        {/* Feature tags */}
                        <div className="flex flex-wrap gap-1.5 pt-1.5 mt-auto">
                            {type.features.map((feature) => (
                                <span
                                    key={feature}
                                    className={`
                  ${compact ? "text-[10px] px-2 py-0.5" : "text-xs px-2.5 py-1"}
                  font-medium rounded-full transition-colors
                  ${isSelected
                                            ? `bg-white/80 text-gray-700 shadow-sm`
                                            : "bg-gray-100 text-gray-500 group-hover/body:bg-white/80 group-hover/body:text-gray-700"
                                        }
                `}
                                >
                                    {feature}
                                </span>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Tutorial — separate hover (opacity only), does not affect card hover */}
                {showTutorial && tutorial && (
                    <div className={`${compact ? "px-4 pb-4" : "px-6 pb-6"} pt-0 relative z-10`}>
                        <div className="border-t border-teal-100 pt-2.5">
                            <button
                                type="button"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setTutorialOpen(true);
                                }}
                                className={`
                  w-full inline-flex items-center justify-center gap-1.5
                  rounded-xl font-medium
                  bg-teal-100 text-teal-800 border border-teal-300
                  hover:bg-teal-200 hover:border-teal-400 hover:text-teal-900
                  active:bg-teal-200/90
                  transition-colors duration-200
                  ${compact ? "h-9 text-xs px-2" : "h-10 text-sm px-3"}
                `}
                                aria-label={tutorial.title}
                            >
                                <GraduationCap className={`${compact ? "w-3.5 h-3.5" : "w-4 h-4"} text-teal-700`} />
                                Tutorial
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {showTutorial && tutorial && (
                <TutorialVideoModal
                    open={tutorialOpen}
                    onOpenChange={setTutorialOpen}
                    youtubeId={tutorial.youtubeId}
                    title={tutorial.title}
                />
            )}
        </>
    );
};
